import { Helmet } from "react-helmet-async";

const ORG_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Lumotis",
  alternateName: "Lumotis Digital Media",
  url: "https://lumotis.com",
  logo: "https://lumotis.com/logo.png",
  description:
    "Full-stack digital marketing agency. AI Search Optimization (GEO/AEO), SEO, Local SEO, Paid Advertising, Social Media, Email Marketing and more — all under one roof.",
  address: {
    "@type": "PostalAddress",
    addressCountry: "US",
  },
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "customer service",
    availableLanguage: "English",
  },
  sameAs: [
    "https://linkedin.com/company/lumotis",
    "https://twitter.com/lumotis",
    "https://instagram.com/lumotis",
    "https://youtube.com/@lumotis",
    "https://facebook.com/lumotis",
  ],
  areaServed: ["US", "GB", "AU", "AE", "CA", "DE", "JP", "SG", "IN"],
  knowsAbout: [
    "AI Search Optimization",
    "Generative Engine Optimization",
    "Search Engine Optimization",
    "Local SEO",
    "Paid Advertising",
    "Social Media Marketing",
    "Email Marketing",
    "Content Marketing",
    "Marketing Automation",
    "Ecommerce Marketing",
    "Real Estate Marketing",
  ],
};

const SERVICE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Digital Marketing Services",
  provider: { "@type": "Organization", name: "Lumotis" },
  serviceType: "Digital Marketing",
  description:
    "Full-stack digital marketing including AI Search Optimization (GEO/AEO), SEO, Local SEO, Paid Ads (Google & Meta), Social Media Management, Email & SMS Marketing, and more.",
  areaServed: "Worldwide",
  url: "https://lumotis.com",
};

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What is AI Search Optimization (GEO/AEO)?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "AI Search Optimization (also called Generative Engine Optimization or Answer Engine Optimization) is the process of making your brand visible in AI-generated answers from ChatGPT, Gemini, Google AI Overviews, and Perplexity. Lumotis structures your content and builds citations so AI systems recommend your business.",
      },
    },
    {
      "@type": "Question",
      name: "What industries does Lumotis specialize in?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Lumotis has built proven marketing playbooks across 15+ industries including Shopify & ecommerce, real estate, salons & spas, healthcare, restaurants, finance, education, and professional services.",
      },
    },
    {
      "@type": "Question",
      name: "How long does digital marketing take to show results?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Paid advertising (PPC) can generate results within days. SEO and Local SEO typically show meaningful traffic growth within 3–6 months. Social media and email marketing compound over time, with strong engagement usually visible within 60–90 days.",
      },
    },
    {
      "@type": "Question",
      name: "Does Lumotis offer full-service digital marketing?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. Lumotis provides a complete digital marketing stack: AI Search Optimization, SEO, Local SEO, Paid Advertising (Google & Meta), Social Media Management, Content Marketing, Email & SMS Marketing, Marketing Automation, Website Design, CRO, Video Content, and Reputation Management.",
      },
    },
  ],
};

const BREADCRUMB_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: "https://lumotis.com",
    },
  ],
};

export default function SeoHead() {
  return (
    <Helmet>
      <html lang="en" />
      <title>
        Lumotis — Full-Stack Digital Marketing Agency | AI Search, SEO, Paid Ads & More
      </title>
      <meta
        name="description"
        content="Lumotis is a full-stack digital marketing agency specializing in AI Search Optimization (GEO/AEO), SEO, Local SEO, Paid Advertising, Social Media, and Email Marketing. We grow revenue for ecommerce brands, real estate agencies, salons, and 15+ other industries. Get a free marketing audit today."
      />
      <meta
        name="keywords"
        content="digital marketing agency, AI search optimization, GEO AEO, generative engine optimization, SEO agency, local SEO, paid advertising agency, Google Ads agency, Meta Ads agency, social media management, email marketing, ecommerce marketing, shopify marketing, real estate marketing, salon marketing, content marketing, marketing automation, full stack digital marketing"
      />
      <link rel="canonical" href="https://lumotis.com" />
      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow" />

      {/* Open Graph */}
      <meta
        property="og:title"
        content="Lumotis — Full-Stack Digital Marketing Agency"
      />
      <meta
        property="og:description"
        content="AI Search, SEO, Local SEO, Paid Ads, Social Media & Email Marketing under one roof. Get a free marketing audit."
      />
      <meta property="og:url" content="https://lumotis.com" />
      <meta property="og:site_name" content="Lumotis" />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:image" content="https://lumotis.com/og-image.jpg" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta
        name="twitter:title"
        content="Lumotis — Full-Stack Digital Marketing Agency"
      />
      <meta
        name="twitter:description"
        content="AI Search, SEO, Paid Ads, Social Media & Email Marketing. Get a free audit."
      />
      <meta
        name="twitter:image"
        content="https://lumotis.com/twitter-image.jpg"
      />

      <script type="application/ld+json">{JSON.stringify(ORG_SCHEMA)}</script>
      <script type="application/ld+json">
        {JSON.stringify(SERVICE_SCHEMA)}
      </script>
      <script type="application/ld+json">{JSON.stringify(FAQ_SCHEMA)}</script>
      <script type="application/ld+json">
        {JSON.stringify(BREADCRUMB_SCHEMA)}
      </script>
    </Helmet>
  );
}
